import React, { useEffect, useMemo } from 'react'

import { inject, observer } from 'mobx-react'

import AnalyticsSettingsStore from '~/client/src/desktop/stores/domain/AnalyticsSettings.store'
import DeliveryDetails from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails'
import * as Layout from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import WorkflowViewFormWrapper from '~/client/src/shared/components/SitePermitCreationForm/WorkflowViewFormWrapper'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import useWorkflows from '~/client/src/shared/hooks/useWorkflows'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveryStore from '~/client/src/shared/stores/domain/Deliveries.store'
import DeliveryAssignmentsStore from '~/client/src/shared/stores/domain/DeliveryAssignments.store'
import DeliveryVehicleTypeStore from '~/client/src/shared/stores/domain/DeliveryVehicleTypes.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialCategoryStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ScanHistoriesStore from '~/client/src/shared/stores/domain/ScanHistories.store'
import SharedDeliveriesStore from '~/client/src/shared/stores/domain/SharedDeliveries.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import DesktopFileInput from '../../components/FileInput/DesktopFileInput'
import DesktopInitialState from '../../stores/DesktopInitialState'
import DesktopEventStore from '../../stores/EventStore/DesktopEvents.store'
import DeliveryActionSideBase from '../Deliveries/components/DeliveryActionSideWrapper/components/DeliveryActionSideBase'
import ScanStationReportsDialog from './components/ReportsDialog/ScanStationReportsDialog'
import ReportsDropdown from './components/ReportsDropdown/ReportsDropdown'
import ReportsHeaderBar from './components/ReportsHeader/ReportsHeader'
import DeliveryReportList from './components/ReportsList/DeliveryReport/DeliveryReportList'
import DeliveryReportListStore from './components/ReportsList/DeliveryReport/DeliveryReportList.store'
import FormReportList from './components/ReportsList/FormReportList/FormReportList'
import FormReportListStore from './components/ReportsList/FormReportList/FormReportList.store'
import ScanStationList from './components/ReportsList/ScanStation/ScanStationList'
import ScanStationListStore from './components/ReportsList/ScanStation/ScanStationList.store'
import ReportsStore from './Reports.store'

import './Reports.scss'

interface IProps {
  state?: DesktopInitialState
  eventsStore?: DesktopEventStore
  scanHistoriesStore?: ScanHistoriesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  projectDateStore?: ProjectDateStore
  workflowTypesStore?: WorkflowTypesStore
  analyticsSettingsStore?: AnalyticsSettingsStore
  deliveriesStore?: DeliveryStore
  materialCategoryStore?: MaterialCategoryStore
  deliveryVehicleTypesStore?: DeliveryVehicleTypeStore
  materialsStore?: MaterialStore
  locationAttributesStore?: LocationAttributesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  deliveryAssignmentsStore?: DeliveryAssignmentsStore
  projectsStore?: ProjectsStore
  sharedDeliveriesStore?: SharedDeliveriesStore
}

const Reports: React.FC<IProps> = inject(
  'state',
  'eventsStore',
  'scanHistoriesStore',
  'companiesStore',
  'projectMembersStore',
  'userProjectsStore',
  'projectDateStore',
  'workflowTypesStore',
  'analyticsSettingsStore',
  'deliveriesStore',
  'materialCategoryStore',
  'deliveryVehicleTypesStore',
  'materialsStore',
  'locationAttributesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'deliveryAssignmentsStore',
  'projectsStore',
  'sharedDeliveriesStore',
)(
  observer(props => {
    const {
      state,
      eventsStore,
      scanHistoriesStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      projectDateStore,
      workflowTypesStore,
      analyticsSettingsStore,
      deliveriesStore,
      materialCategoryStore,
      deliveryVehicleTypesStore,
      materialsStore,
      locationAttributesStore,
      permitInspectionsStore,
      permitInspectionChangesStore,
      deliveryAssignmentsStore,
      projectsStore,
      sharedDeliveriesStore,
    } = props

    const { workflows, workflowsLoading } = useWorkflows(
      state.activeProject?.id || '',
      state.user.id,
      {},
    )

    const store = useMemo(
      () =>
        new ReportsStore(
          eventsStore,
          scanHistoriesStore,
          workflowTypesStore,
          analyticsSettingsStore,
          deliveriesStore,
          projectDateStore,
        ),
      [
        eventsStore,
        scanHistoriesStore,
        workflowTypesStore,
        analyticsSettingsStore,
        deliveriesStore,
        projectDateStore,
      ],
    )

    const scanStationListStore = useMemo(
      () =>
        new ScanStationListStore(
          state,
          store,
          companiesStore,
          projectMembersStore,
          userProjectsStore,
          projectDateStore,
        ),
      [
        state,
        store,
        companiesStore,
        projectMembersStore,
        userProjectsStore,
        projectDateStore,
      ],
    )

    const projectId = state.activeProject?.id
    const { records: materialResourceRecords } = useMaterialResources({
      projectId,
    })

    const formReportListStore = useMemo(
      () =>
        new FormReportListStore(
          state,
          store,
          companiesStore,
          workflowTypesStore,
          projectDateStore,
          permitInspectionsStore,
          permitInspectionChangesStore,
          projectMembersStore,
          locationAttributesStore,
          materialsStore,
        ),
      [
        state,
        store,
        companiesStore,
        workflowTypesStore,
        projectDateStore,
        permitInspectionsStore,
        permitInspectionChangesStore,
        projectMembersStore,
        locationAttributesStore,
        materialsStore,
      ],
    )

    useEffect(() => {
      formReportListStore.setMaterialResourceRecords(materialResourceRecords)
    }, [formReportListStore, materialResourceRecords])

    const deliveryReportListStore = useMemo(
      () =>
        new DeliveryReportListStore(
          state,
          store,
          companiesStore,
          projectDateStore,
          locationAttributesStore,
          deliveryVehicleTypesStore,
          materialsStore,
          projectMembersStore,
          userProjectsStore,
          materialCategoryStore,
          deliveryAssignmentsStore,
          projectsStore,
          sharedDeliveriesStore,
        ),
      [
        state,
        store,
        companiesStore,
        projectDateStore,
        locationAttributesStore,
        deliveryVehicleTypesStore,
        materialsStore,
        projectMembersStore,
        userProjectsStore,
        materialCategoryStore,
        deliveryAssignmentsStore,
        projectsStore,
        sharedDeliveriesStore,
      ],
    )

    useEffect(() => {
      store.setWorkflows(workflows)
    }, [store, workflows])

    if (!store.isDataPrepared || workflowsLoading)
      return <Loader hint={Localization.translator.loading} />

    const {
      isScanStationDialogDisplayed,
      isFormReportDialogDisplayed,
      isDeliveryReportDialogDisplayed,
      selectedFormReport,
      selectedDeliveryReport,
      closeDeliveryReports,
      closeFormReports,
    } = store

    const renderContent = () => {
      const { isScanStation, isFormReport, isDeliveryReport } = store
      return (
        <>
          {isScanStation && <ScanStationList store={scanStationListStore} />}
          {isFormReport && <FormReportList store={formReportListStore} />}
          {isDeliveryReport && (
            <DeliveryReportList store={deliveryReportListStore} />
          )}
        </>
      )
    }

    return (
      <Layout.View className="overflow-hidden h-fit-content reports-layout-view">
        <Layout.Header className="reports-header col no-grow">
          <div className="row x-between">
            <ReportsDropdown store={store} />
          </div>
          <ReportsHeaderBar
            reportsStore={store}
            scanStationListStore={scanStationListStore}
            formReportListStore={formReportListStore}
            deliveryReportListStore={deliveryReportListStore}
          />
        </Layout.Header>
        <Layout.Content className="row full-height">
          <div className="relative-block overflow-hidden multi-grid-container">
            <div className="reports-list-container full-height">
              {isScanStationDialogDisplayed && (
                <div className="report-side-view absolute full-height bg-white">
                  <ScanStationReportsDialog store={store} />
                </div>
              )}
              {isFormReportDialogDisplayed && (
                <div className="report-side-view absolute full-height bg-white">
                  <WorkflowViewFormWrapper
                    key={selectedFormReport.id}
                    workflowToShow={selectedFormReport}
                    onClose={closeFormReports}
                    FileInputType={DesktopFileInput}
                  />
                </div>
              )}
              {isDeliveryReportDialogDisplayed && (
                <DeliveryActionSideBase
                  title={EMPTY_STRING}
                  content={
                    <DeliveryDetails
                      initProjectId={state.activeProject.id}
                      FileInputType={DesktopFileInput}
                      backClicked={closeDeliveryReports}
                      displayedDeliveryId={selectedDeliveryReport?.id}
                    />
                  }
                  shouldShowHeader={false}
                />
              )}
              <div className="row table-holder">{renderContent()}</div>
            </div>
          </div>
        </Layout.Content>
      </Layout.View>
    )
  }),
)

export default Reports
